import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, message } from "antd";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import {
  Container,
  TextField,
  Button,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import imageCompression from "browser-image-compression";
import { updateTeacher } from "../../redux/actions/admin/teachersAction";

const EditTeacher = ({
  id,
  teacher_TeacherImage,
  teacher_Name,
  teacher_Email,
  teacher_Phone,
  teacher_Subject,
  teacher_Experience,
  teacher_Salary,
  teacher_TeacherPublic,
  refreshData,
}) => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.teacher);
  const [name, setName] = useState(teacher_Name || "");
  const [email, setEmail] = useState(teacher_Email || "");
  const [phone, setPhone] = useState(teacher_Phone || "");
  const [subject, setSubject] = useState(teacher_Subject || "");
  const [experience, setExperience] = useState(teacher_Experience || "");
  const [salary, setSalary] = useState(teacher_Salary || "");
  const [teacherPublic, setTeacherPublic] = useState(
    teacher_TeacherPublic || ""
  );
  const [image, setImage] = useState(teacher_TeacherImage || "");
  const [imageBase64, setImageBase64] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const showModal = () => setIsModalOpen(true);

  const VisuallyHiddenInput = styled("input")({
    display: "none",
  });

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    const maxSizeInMB = 25;

    if (file) {
      if (file.size / (1024 * 1024) > maxSizeInMB) {
        message.error(`Image size should not exceed ${maxSizeInMB} MB.`);
        return;
      }

      const options = {
        maxSizeMB: 25,
        maxWidthOrHeight: 1024,
        useWebWorker: true,
      };

      try {
        const compressedFile = await imageCompression(file, options);
        const imageUrl = URL.createObjectURL(compressedFile);
        setImage(imageUrl);
        setImageBase64(compressedFile);
      } catch (error) {
        console.error("Error compressing image:", error);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name) {
      message.error("Please fill in all required fields.");
      return;
    }

    Modal.confirm({
      title: "Are you sure you want to edit these teacher details?",
      content: "Please confirm to apply the changes made to this teacher",
      okText: "Yes, Change",
      cancelText: "No, Cancel",
      onOk: async () => {
        const formData = new FormData();
        formData.append("id", id);
        formData.append("name", name);
        formData.append("phone", phone);
        formData.append("email", email);
        formData.append("subject", subject);
        formData.append("experience", experience);
        formData.append("salary", salary);
        formData.append("teacherPublic", teacherPublic);

        if (imageBase64) {
          formData.append("teacherImage", imageBase64); // Directly file upload
        }

        await dispatch(updateTeacher(formData));
        refreshData();
      },
    });
  };

  return (
    <>
      <IconButton aria-label="edit" onClick={showModal}>
        <EditIcon sx={{ color: "#1976d2" }} />
      </IconButton>

      <Modal
        title="Edit Teacher Details"
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}
      >
        <Container
          onSubmit={handleSubmit}
          component="form"
          className="editTeacher-form_style border-style"
          maxWidth="sm"
        >
          <TextField
            fullWidth
            id="teacherName"
            label="Teacher Name *"
            placeholder="Teacher Name *"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ mb: 3 }}
          />

          <TextField
            sx={{
              mb: 3,
              "& .MuiInputBase-root": {
                color: "text.secondary",
              },
              fieldset: { borderColor: "rgb(231, 235, 240)" },
            }}
            fullWidth
            id="teacherEmail"
            label="Teacher Email *"
            name="teacherEmail"
            InputLabelProps={{
              shrink: true,
            }}
            placeholder="Teacher Email *"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <TextField
            sx={{
              mb: 3,
              "& .MuiInputBase-root": {
                color: "text.secondary",
              },
              "& input[type='number']": {
                MozAppearance: "textfield",
                "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                  display: "none",
                },
              },
              fieldset: { borderColor: "rgb(231, 235, 240)" },
            }}
            fullWidth
            id="teacherPhone"
            label="Teacher Phone *"
            name="teacherPhone"
            type="number"
            InputLabelProps={{
              shrink: true,
            }}
            placeholder="Teacher Phone *"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />

          <TextField
            fullWidth
            id="teacherSubject"
            label="Teacher Subject *"
            placeholder="Teacher Subject *"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            sx={{ mb: 3 }}
          />

          <TextField
            sx={{
              mb: 3,
              "& .MuiInputBase-root": {
                color: "text.secondary",
              },
              "& input[type='number']": {
                MozAppearance: "textfield",
                "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                  display: "none",
                },
              },
              fieldset: { borderColor: "rgb(231, 235, 240)" },
            }}
            fullWidth
            id="teacherExperience"
            label="Teacher Experience (Years) *"
            placeholder="Teacher Experience (Years) *"
            type="number"
            value={experience}
            onChange={(e) => setExperience(e.target.value)}
          />

          <TextField
            sx={{
              mb: 3,
              "& .MuiInputBase-root": {
                color: "text.secondary",
              },
              "& input[type='number']": {
                MozAppearance: "textfield",
                "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                  display: "none",
                },
              },
              fieldset: { borderColor: "rgb(231, 235, 240)" },
            }}
            fullWidth
            id="teacherSalary"
            label="Teacher Salary (Monthly)"
            name="teacherSalary"
            type="number"
            InputLabelProps={{
              shrink: true,
            }}
            placeholder="Teacher Salary (Monthly)"
            value={salary}
            onChange={(e) => setSalary(e.target.value)}
          />

          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel id="teacher-public-label *">Teacher Public</InputLabel>
              <Select
                labelId="teacher-public-label *"
                id="teacher-public"
                value={teacherPublic}
                label="Teacher Public *"
                onChange={(e) => setTeacherPublic(e.target.value)}
              >
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box>
            <VisuallyHiddenInput
              accept="image/*"
              id="contained-button-file"
              type="file"
              onChange={handleImageUpload}
            />
            <label htmlFor="contained-button-file">
              <Button
                variant="outlined"
                color="primary"
                component="span"
                startIcon={<CloudUploadIcon />}
              >
                Upload Image
              </Button>
            </label>
            {image && (
              <Box mt={3} display="flex" alignItems="center">
                <img
                  src={image}
                  alt="avatar"
                  style={{
                    height: "100px",
                    width: "100px",
                    borderRadius: "50%",
                    objectFit: "cover",
                  }}
                />
              </Box>
            )}
          </Box>
          <Button
            variant="contained"
            type="submit"
            // fullWidth
            disabled={loading}
            sx={{ mt: 3 }}
          >
            {loading ? <CircularProgress size={24} /> : "Update Teacher"}
          </Button>
        </Container>
      </Modal>
    </>
  );
};

export default EditTeacher;
